import { Car, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import CrudPage from "@/components/CrudPage";
import { StatusBadge, ConfirmDelete } from "@/components/common";
import { dmy, todayISO, addDays, VEHICLE_TYPES } from "@/lib/format";

const IMG = {
  dzire: "https://images.unsplash.com/photo-1674476459501-47466da1dbbc?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
  default: "https://images.unsplash.com/photo-1464708042292-1323f62d4e3b?crop=entropy&cs=srgb&fm=jpg&q=80&w=900",
};

const DOCS = [
  ["insurance_expiry", "Insurance"], ["puc_expiry", "PUC"], ["permit_expiry", "Permit"],
  ["fitness_expiry", "Fitness"], ["tax_expiry", "Road Tax"], ["service_due_date", "Service Due"],
];

const sections = [
  {
    title: "Vehicle",
    fields: [
      { key: "vehicle_number", label: "Registration Number", required: true, placeholder: "MH-20-XX-0000" },
      { key: "model", label: "Make & Model", type: "select", options: ["Maruti Suzuki Dzire", "Hyundai Aura", "Other"] },
      { key: "vehicle_type", label: "Vehicle Type", type: "select", options: VEHICLE_TYPES },
      { key: "fuel_type", label: "Fuel", type: "select", options: ["Petrol", "Diesel", "CNG", "Petrol + CNG", "EV"] },
      { key: "year", label: "Year", type: "number" },
      { key: "color", label: "Colour" },
      { key: "chassis_no", label: "Chassis No" },
      { key: "current_km", label: "Current Odometer (KM)", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
    ],
  },
  { title: "Driver", fields: [{ key: "driver_name", label: "Assigned Driver" }, { key: "driver_mobile", label: "Driver Mobile", type: "tel" }, { key: "driver_salary", label: "Monthly Salary (₹)", type: "number" }] },
  {
    title: "Documents & Expiry",
    fields: [{ key: "insurance_company", label: "Insurance Company" }, { key: "insurance_policy_no", label: "Policy No" },
      ...DOCS.map(([key, label]) => ({ key, label: `${label} ${key === "service_due_date" ? "Date" : "Expiry"}`, type: "date" }))],
  },
];

function DocChip({ label, date }) {
  if (!date) return <div className="text-xs rounded-md border px-2 py-1.5 text-muted-foreground"><div className="font-semibold">{label}</div>Not set</div>;
  const t = todayISO();
  const tone = date < t ? "border-rose-300 bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:border-rose-900 dark:text-rose-400" : date <= addDays(t, 30) ? "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-400" : "";
  return <div className={`text-xs rounded-md border px-2 py-1.5 ${tone}`}><div className="font-semibold flex items-center gap-1">{label}{tone && <AlertTriangle className="h-3 w-3" />}</div><span className="num">{dmy(date)}</span></div>;
}

function VehicleCard(v, { edit, remove }) {
  return (
    <div key={v.id} className="rounded-xl border bg-card overflow-hidden hover:shadow-md transition-shadow" data-testid={`vehicles-card-${v.id}`}>
      <div className="relative h-40 bg-slate-900">
        <img src={/dzire/i.test(v.model || "") ? IMG.dzire : IMG.default} alt={v.model} className="w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 to-transparent" />
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
          <div>
            <div className="font-mono font-bold text-white text-lg tracking-wide">{v.vehicle_number}</div>
            <div className="text-xs text-slate-300">{v.model} {v.fuel_type ? `· ${v.fuel_type}` : ""}</div>
          </div>
          <StatusBadge value={v.status || "Active"} />
        </div>
      </div>
      <div className="p-4">
        <div className="text-sm"><span className="text-muted-foreground">Driver:</span> <span className="font-medium">{v.driver_name || "-"}</span> {v.driver_mobile && <span className="text-xs text-muted-foreground">· {v.driver_mobile}</span>}</div>
        <div className="grid grid-cols-3 gap-2 mt-3">{DOCS.map(([k, l]) => <DocChip key={k} label={l} date={v[k]} />)}</div>
        <div className="flex justify-end gap-1 mt-3">
          <Button variant="ghost" size="sm" onClick={edit} data-testid={`vehicles-edit-${v.id}`}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
          <ConfirmDelete onConfirm={remove} trigger={<Button variant="ghost" size="sm" className="text-rose-600" data-testid={`vehicles-delete-${v.id}`}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>} />
        </div>
      </div>
    </div>
  );
}

export default function Vehicles() {
  return (
    <CrudPage resource="vehicles" eyebrow="Masters" title="Own Vehicles" singular="Vehicle" icon={Car}
      subtitle="Your fleet — drivers, documents and expiry tracking. Profit per vehicle appears on the dashboard."
      sections={sections} columns={[]} renderCard={VehicleCard} defaults={{ status: "Active", vehicle_type: VEHICLE_TYPES[0], fuel_type: "Petrol + CNG" }} />
  );
}
